// The chain runner's context at the table (issue 1499, part 2; docs/MRCS/workbench.md §4.1, §7): every Direct a block
// reads, answered from the table's own state and the numbers the server read for this act. Pure: the server reads the
// site (a monster's stat block, its defences) BEFORE the commit and hands the numbers in, so the commit stays a pure
// function of its inputs and a replay reads the record, never this.
//
//   sheet      the numbers the table holds for the creature's token (a linked character's are read onto it from its
//              sheet): `armor_class` its AC, `hp_current` and `hp_max` its hit points, `initiative` its bonus; every
//              other purpose from the sheet's numbers the token carries (`values.sheet`, part 3): with `which`, the
//              field of that purpose whose key or label has that word (`strength` is "Strength total", `reflex` the
//              "Reflex" save); without, the first of that purpose. None: not known.
//              A number of the sheet's that is dice (a weapon's damage, qa-runs 264) is answered as dice. A token linked
//              to a stat block answers from it too (`facts.sheet`, read by the server: its ability scores and modifiers,
//              its first melee attack's damage, `statBlockSheet`), after anything the token carries itself.
//   stat       the stat block the token is linked to, by register (`stats`, read by the server); none is not known.
//   defence    as `attack.ts` reads a defence: the main one (the token's armour class) from the token, any other from
//              the monster (`defences`, read by the server).
//   condition  yes when the token's conditions, or an effect on its combatant, name `key`: a name read as a slug
//              ("Flat-Footed" is `flat-footed`, an effect "Raging" is `raging`); else no.
//   board      `distance` in the system's unit (squares with none) between the two tokens' squares, by its diagonal
//              rule; `adjacent` when that is one square or less. The others (`squares`, `flanked`, `in_range`,
//              `in_area`) are not measured yet: not known.
//   attack     (issue 1508) the attack being made, for an entry started before its roll: `ranged`, `two_handed` and
//              `off_hand` as the attack says (absent is no), `full` when it is a swing of a full attack, `light` only
//              when it says; with no attack, not known.
// A `choose` End's creatures (issue 1508) are the enemies of a creature (the auras' side rule, `relation`: never a
// Neutral, qa-runs#1157) on the scene,
// standing, next to another when the End says so, and only those every player could see (`seen`): a button never names
// a creature hidden from the person asked. Each is named as the board names it to a player, else "Unnamed creature 1".
import { abilityKeyOf, abilityLossRow } from './specials.js';
import { SHEET_NUMBER_LIMITS, parseSheetNumbers } from '../state/objects.js';
import { parseDamageText } from './attack.js';
import { squaresApart } from './weaponReach.js';
import { relation } from '../state/auras.js';
/** The system's default rounding out of `features.rounding` (§3.4): `{ default: 'down' }`; anything else is none. */
export function parseRounding(v) {
    const d = typeof v === 'object' && v !== null && !Array.isArray(v) ? v.default : undefined;
    return d === 'down' || d === 'up' || d === 'half_up' ? d : null;
}
/** A condition's or effect's name as a block names it: lower case, words joined by "-". */
export const conditionKey = (name) => name.trim().toLowerCase().replace(/[^a-z0-9._]+/g, '-').replace(/^-+|-+$/g, '');
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const FACT_IDS = 16;
const KIND_IDS = 128;
/** Stored chain facts (qa-runs 264: on a trigger's record), exactly: at most 16 creatures each, numbers only; else null. */
export function parseChainFacts(v) {
    if (!isRecord(v) || Object.keys(v).some((k) => k !== 'stats' && k !== 'defences' && k !== 'sheet' && k !== 'kinds' && k !== 'raises'))
        return null;
    const numbers = (x, nullable) => isRecord(x) && Object.keys(x).length <= FACT_IDS
        && Object.values(x).every((m) => isRecord(m) && Object.keys(m).length <= 64 && Object.values(m).every((n) => (nullable && n === null) || (typeof n === 'number' && Number.isFinite(n))));
    if (v.stats !== undefined && !numbers(v.stats, false))
        return null;
    if (v.defences !== undefined && !numbers(v.defences, true))
        return null;
    if (v.raises !== undefined && !numbers(v.raises, false))
        return null;
    if (v.sheet !== undefined && !(isRecord(v.sheet) && Object.keys(v.sheet).length <= FACT_IDS && Object.values(v.sheet).every((l) => parseSheetNumbers(l) !== null)))
        return null;
    if (v.kinds !== undefined && !(isRecord(v.kinds) && Object.keys(v.kinds).length <= KIND_IDS && Object.values(v.kinds).every((k) => isRecord(k) && (k.kind === null || (typeof k.kind === 'string' && k.kind.length <= 64)) && (k.hd === null || (typeof k.hd === 'number' && Number.isFinite(k.hd))) && (k.type === undefined || k.type === null || (typeof k.type === 'string' && k.type.length <= 64)))))
        return null;
    return JSON.parse(JSON.stringify(v));
}
/** An ability's words as a sheet field's (`str`, `strength`): its key's and its label's, lower case. */
const wordsOf = (...texts) => [...new Set(texts.flatMap((t) => t.toLowerCase().split(/[^a-z0-9]+/)).filter((w) => w.length >= 1 && w.length <= SHEET_NUMBER_LIMITS.word))].slice(0, SHEET_NUMBER_LIMITS.words);
/**
 * A stat block's numbers as a sheet's (qa-runs 264): each ability score (`ability_score`, its words the attribute's key
 * and label, so `which: strength` finds "Str"/"Strength") and its modifier (`ability_modifier`), then the damage of its
 * first melee attack with dice (`damage`, as dice), so a block that reads a creature's sheet reads a linked monster's.
 */
export function statBlockSheet(abilities, attacks) {
    const out = [];
    for (const a of abilities ?? []) {
        const words = wordsOf(a.key, a.label);
        if (!words.length || !Number.isFinite(a.score) || !Number.isFinite(a.modifier))
            continue;
        out.push({ purpose: 'ability_score', words, value: a.score }, { purpose: 'ability_modifier', words, value: a.modifier });
    }
    for (const at of attacks ?? []) {
        const d = !at.ranged && at.damage ? parseDamageText(at.damage) : null;
        const dice = d?.parts.filter((p) => !p.extra).map((p) => ({ count: p.count, sides: p.sides }));
        if (!d || !dice?.length)
            continue;
        out.push({ purpose: 'damage', words: ['damage'], value: d.bonus, dice });
        break;
    }
    return out.slice(0, SHEET_NUMBER_LIMITS.count);
}
/** A sheet number as a Direct's value: dice when it holds dice (qa-runs 264), else the number. */
const sheetValue = (n) => (n.dice?.length ? { dice: n.dice.map((p) => ({ count: p.count, sides: p.sides })), bonus: n.value } : n.value);
/** The names a token carries: its conditions and the effects on its combatant. */
export function carriedNames(scene, id) {
    const o = scene.objects[id];
    if (!o)
        return [];
    const effects = scene.encounter.combatants.filter((c) => c.tokens.includes(id)).flatMap((c) => c.effects.map((e) => e.label));
    return [...(o.values.conditions ?? []), ...effects];
}
/** The `save` numbers on a creature's combatant: the best of each bonus type, every penalty and untyped number added. */
function effectSaves(scene, id, which) {
    const mods = scene.encounter.combatants.filter((c) => c.tokens.includes(id)).flatMap((c) => c.effects.flatMap((e) => e.mods ?? [])).filter((m) => m.target === 'save' && (!m.which || m.which === which));
    const best = new Map();
    let sum = 0;
    for (const m of mods) {
        if (!m.type || m.value < 0)
            sum += m.value;
        else
            best.set(m.type, Math.max(best.get(m.type) ?? 0, m.value));
    }
    return sum + [...best.values()].reduce((x, y) => x + y, 0);
}
/** What a rule reads of a creature's kind and type (`creature_types`), from the record's facts: a run's and a resumed one's. */
export function kindReads(facts, combat, objects) {
    return {
        isKind: (id, word) => {
            const k = facts?.kinds?.[id];
            const c = combat?.creatureTypes;
            if (!k || !c)
                return null;
            if (word === c.otherwise || word === c.character || word in c.kinds)
                return k.kind === null ? null : k.kind === word;
            return k.type ? k.type === word || k.type.startsWith(`${word} `) : null;
        },
        kindKnown: (id) => { const k = facts?.kinds?.[id]; return !!k && k.kind !== null && k.hd !== null; },
        nameOf: (id) => objects[id]?.name.slice(0, 40) ?? null,
    };
}
export function tableChainContext(a) {
    const sheet = (id, purpose) => {
        const v = a.scene.objects[id]?.values;
        if (!v)
            return null;
        switch (purpose) {
            case 'armor_class': return v.ac ?? null;
            case 'hp_current': return v.hp?.current ?? null;
            case 'hp_max': return v.hp?.max ?? null;
            case 'initiative': return v.initBonus ?? null;
            default: return null;
        }
    };
    const squares = (from, to) => {
        const p = a.scene.objects[from];
        const q = to === undefined ? undefined : a.scene.objects[to];
        if (!p || !q || !a.extent)
            return null;
        // Nearest square to nearest square, height counted (qa-runs#1160; design/32 §3): as the table measures reach and range.
        return squaresApart(a.scene, a.extent, p, q, a.distance?.diagonal ?? 'one');
    };
    const direct = (ask) => {
        if (ask.source === 'attack') {
            const at = a.attack;
            if (!at)
                return null;
            if (ask.key === 'light')
                return at.light ?? null;
            return ask.key === 'ranged' || ask.key === 'two_handed' || ask.key === 'off_hand' || ask.key === 'full' ? at[ask.key] === true : null;
        }
        if (!a.scene.objects[ask.creature])
            return null;
        switch (ask.source) {
            // The feats its sheet lists count ("a creature has Improved Vital Strike"), a trailing ": choice" left off.
            case 'condition': return [...carriedNames(a.scene, ask.creature), ...(a.scene.objects[ask.creature].values.feats ?? []).map((f) => f.replace(/\s*:[^:]*$/, ''))].some((n) => conditionKey(n) === ask.key);
            case 'sheet': {
                const own = ask.which === undefined ? sheet(ask.creature, ask.purpose) : null;
                if (own !== null)
                    return own;
                const which = ask.which?.toLowerCase();
                const match = (n) => n.purpose === ask.purpose && (which === undefined || n.words.includes(which));
                const found = (a.scene.objects[ask.creature].values.sheet ?? []).find(match) ?? (a.facts?.sheet?.[ask.creature] ?? []).find(match);
                if (!found)
                    return null;
                // What the effects on it add to a save (Prayer's +1): a save a rule throws counts them as the area flow does.
                const value = sheetValue(found);
                // A score that ability damage or drain lowered (`AbilityLossRule`), and the modifier with it.
                const rule = a.combat?.specials?.abilityLoss;
                const loss = a.scene.objects[ask.creature].values.abilityLoss;
                const key = rule && loss && which && (ask.purpose === 'ability_score' || ask.purpose === 'ability_modifier') ? abilityKeyOf(rule, which) : null;
                if (rule && key && loss?.[key] && typeof value === 'number') {
                    const read = (n) => n.purpose === 'ability_score' && n.words.includes(which);
                    const score = ask.purpose === 'ability_score' ? value : (a.scene.objects[ask.creature].values.sheet ?? []).find(read)?.value ?? (a.facts?.sheet?.[ask.creature] ?? []).find(read)?.value ?? null;
                    const row = abilityLossRow(rule, key, loss[key], score);
                    return ask.purpose === 'ability_score' ? row.score ?? value : value - row.drop;
                }
                return ask.purpose === 'saving_throw' && typeof value === 'number' ? value + effectSaves(a.scene, ask.creature, which) : value;
            }
            case 'stat': return a.facts?.stats?.[ask.creature]?.[ask.register] ?? null;
            case 'defence': {
                const known = ask.key === a.mainDefence ? a.scene.objects[ask.creature].values.ac ?? null
                    : a.facts?.defences?.[ask.creature]?.[ask.key] ?? a.scene.objects[ask.creature].values.defences?.[ask.key] ?? null;
                return known === null ? null : known + (a.facts?.raises?.[ask.creature]?.[ask.key] ?? 0);
            }
            case 'board': {
                if (ask.measure !== 'distance' && ask.measure !== 'adjacent')
                    return null;
                const n = squares(ask.creature, ask.to);
                if (n === null)
                    return null;
                if (ask.measure === 'adjacent')
                    return n <= 1;
                return a.distance ? n * a.distance.perSquare : n;
            }
            default: return null;
        }
    };
    // A `choose` End's creatures (issue 1508): the standing enemies of `of` the players could see, next to `adjacentTo`.
    const reached = (ask) => {
        if (!a.scene.objects[ask.of])
            return null;
        const per = a.distance?.perSquare ?? 1;
        return Object.values(a.scene.objects)
            .filter((o) => o.kind === 'token' && !o.dead)
            .map((o) => ({ o, feet: (squares(ask.of, o.id) ?? Infinity) * per }))
            .filter((x) => x.feet <= ask.within)
            .sort((x, y) => x.feet - y.feet || x.o.id.localeCompare(y.o.id))
            .map(({ o }) => ({ id: o.id, name: o.name.slice(0, 40), kind: a.facts?.kinds?.[o.id]?.kind ?? null, hd: a.facts?.kinds?.[o.id]?.hd ?? null, carries: carriedNames(a.scene, o.id).map(conditionKey) }));
    };
    const creatures = (ask) => {
        const of = a.scene.objects[ask.of];
        if (!of)
            return null;
        const side = (o) => ({ id: o.id, pc: o.pc, owners: o.owners, ...(o.disposition ? { disposition: o.disposition } : {}) });
        let unnamed = 0;
        return Object.values(a.scene.objects)
            // Standing: not dead, and not down at 0 hit points or fewer (issue 1509: the foe Cleave felled). Next to a creature
            // is never that creature itself.
            .filter((o) => o.kind === 'token' && !o.dead && (ask.relation === 'any' || (o.id !== of.id && !((o.values.hp?.current ?? 1) <= 0) && relation(side(of), side(o)) === 'enemy')) && (!a.seen || a.seen(o.id))
            && (ask.adjacentTo === undefined || (ask.relation === 'any' && o.id === ask.adjacentTo) || (o.id !== ask.adjacentTo && (squares(ask.adjacentTo, o.id) ?? 2) <= 1))
            && (ask.within === undefined || ((squares(of.id, o.id) ?? Infinity) * (a.distance?.perSquare ?? 1) <= ask.within)))
            // Named ones by name, then the unnamed by id: the order says nothing of a name that is not shown.
            .map((o) => ({ o, shown: o.pc || o.shares.name }))
            .sort((x, y) => (x.shown === y.shown ? (x.shown ? x.o.name.localeCompare(y.o.name) : 0) || x.o.id.localeCompare(y.o.id) : x.shown ? -1 : 1))
            .map(({ o, shown }) => ({ id: o.id, name: (shown ? o.name : `Unnamed creature ${++unnamed}`).slice(0, 40) }));
    };
    return {
        resolve: (r) => a.library.find((b) => b.id === r.block && b.version === r.version) ?? null,
        direct,
        creatures,
        reached,
        kindOf: (id) => a.facts?.kinds?.[id]?.kind ?? null,
        ...kindReads(a.facts, a.combat, a.scene.objects),
        isCharacter: (id) => !!a.scene.objects[id]?.character,
        combat: a.combat ? { attack: a.combat.attack, saves: a.combat.saves, minimumDamage: a.combat.minimumDamage, ...(a.combat.minimumDamageKind ? { minimumDamageKind: a.combat.minimumDamageKind } : {}) } : null,
        rounding: a.rounding,
        rng: a.rng,
        ...(a.critical ? { critical: a.critical } : {}),
    };
}
//# sourceMappingURL=chainTable.js.map